import { useState, type ReactNode } from "react";
import type { PublicacaoDto } from "@base/contracts";
import {
  BarChart2,
  Bookmark,
  Globe,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Music2,
  Repeat2,
  Send,
  Share2,
  ThumbsUp,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PreviewMidiasAgendamento,
  type SlidePreviewMidia,
} from "./preview-midias-agendamento";

export type ContaDaPrevia = {
  /** Nome exibido (página, perfil). */
  readonly nome: string;
  /** @ sem o arroba. Null quando a conta não está conectada. */
  readonly usuario: string | null;
  readonly fotoUrl: string | null;
};

type Props = {
  readonly publicacao: PublicacaoDto;
  readonly slides: readonly SlidePreviewMidia[];
  readonly conta: ContaDaPrevia;
};

/**
 * Como o post vai aparecer na rede: cabeçalho da conta, mídia, ações e
 * legenda no formato de cada plataforma. É uma aproximação visual (não é
 * render oficial da rede), para a pessoa conferir antes de sair.
 */
export function PreviaPostNaRede({ publicacao, slides, conta }: Props) {
  if (publicacao.plataforma === "twitter") {
    return <PreviaX publicacao={publicacao} slides={slides} conta={conta} />;
  }
  if (publicacao.plataforma === "facebook") {
    return (
      <PreviaFacebook publicacao={publicacao} slides={slides} conta={conta} />
    );
  }
  if (publicacao.plataforma === "tiktok") {
    return (
      <PreviaTikTok publicacao={publicacao} slides={slides} conta={conta} />
    );
  }
  return (
    <PreviaInstagram publicacao={publicacao} slides={slides} conta={conta} />
  );
}

function Moldura({ children }: { readonly children: ReactNode }) {
  return (
    <article
      aria-label="Prévia do post na rede"
      className="w-full max-w-[340px] overflow-hidden rounded-[22px] border border-border bg-card text-card-foreground shadow-e2"
    >
      {children}
    </article>
  );
}

function Avatar({ conta, tamanho = "size-8" }: {
  readonly conta: ContaDaPrevia;
  readonly tamanho?: string;
}) {
  const inicial = (conta.usuario ?? conta.nome).trim().slice(0, 1).toUpperCase();
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center overflow-hidden rounded-full bg-primary text-xs font-bold text-primary-foreground ring-2 ring-card",
        tamanho,
      )}
      aria-hidden
    >
      {conta.fotoUrl ? (
        <img src={conta.fotoUrl} alt="" className="size-full object-cover" />
      ) : (
        inicial || "R"
      )}
    </span>
  );
}

function Midia({
  publicacao,
  slides,
  aspecto,
  className,
}: {
  readonly publicacao: PublicacaoDto;
  readonly slides: readonly SlidePreviewMidia[];
  readonly aspecto: string;
  readonly className?: string;
}) {
  return (
    <div className={cn("relative w-full bg-black", aspecto, className)}>
      {slides.length > 0 ? (
        <PreviewMidiasAgendamento
          slides={slides}
          tipo={publicacao.tipo}
          incorporado
        />
      ) : (
        <div className="grid size-full place-items-center text-xs text-white/60">
          Sem mídia
        </div>
      )}
    </div>
  );
}

/** Legenda com "mais": como as redes cortam texto longo. */
function Legenda({
  autor,
  texto,
  linhas = 2,
}: {
  readonly autor?: string;
  readonly texto: string;
  readonly linhas?: 2 | 3;
}) {
  const [aberta, setAberta] = useState(false);
  const limpo = texto.trim();
  if (!limpo && !autor) return null;
  const longa = limpo.length > 90 || limpo.split("\n").length > linhas;
  return (
    <p
      className={cn(
        "whitespace-pre-wrap break-words text-[13px] leading-[18px] text-foreground",
        !aberta && (linhas === 3 ? "line-clamp-3" : "line-clamp-2"),
      )}
    >
      {autor ? <span className="mr-1.5 font-semibold">{autor}</span> : null}
      {limpo || <span className="text-muted-foreground">Sem legenda</span>}
      {longa && !aberta ? (
        <button
          type="button"
          onClick={() => setAberta(true)}
          className="ml-1 cursor-pointer text-muted-foreground hover:text-foreground"
        >
          mais
        </button>
      ) : null}
    </p>
  );
}

function PreviaInstagram({ publicacao, slides, conta }: Props) {
  const usuario = conta.usuario ?? conta.nome;
  const ehReels = publicacao.tipo === "reels";
  return (
    <Moldura>
      <header className="flex items-center gap-2.5 px-3 py-2.5">
        <Avatar conta={conta} />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-[13px] font-semibold">
            {usuario}
            {publicacao.colaboradores.length > 0 ? (
              <span className="font-normal text-muted-foreground">
                {" "}e @{publicacao.colaboradores[0]}
              </span>
            ) : null}
          </p>
          {ehReels ? (
            <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <Music2 className="size-3" aria-hidden /> Áudio original
            </p>
          ) : null}
        </div>
        <MoreHorizontal className="size-4 text-muted-foreground" aria-hidden />
      </header>
      <Midia
        publicacao={publicacao}
        slides={slides}
        aspecto={ehReels ? "aspect-[9/16] max-h-[420px]" : "aspect-[4/5]"}
      />
      <div className="flex items-center gap-3.5 px-3 pt-2.5 text-foreground" aria-hidden>
        <Heart className="size-[22px]" />
        <MessageCircle className="size-[22px]" />
        <Send className="size-[22px]" />
        <Bookmark className="ml-auto size-[22px]" />
      </div>
      <div className="px-3 pb-3.5 pt-2">
        <Legenda autor={usuario} texto={publicacao.legenda} />
      </div>
    </Moldura>
  );
}

function PreviaFacebook({ publicacao, slides, conta }: Props) {
  return (
    <Moldura>
      <header className="flex items-center gap-2.5 px-3.5 pt-3">
        <Avatar conta={conta} tamanho="size-9" />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-[13px] font-semibold">{conta.nome}</p>
          <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
            Agendado · <Globe className="size-3" aria-hidden />
          </p>
        </div>
        <MoreHorizontal className="size-4 text-muted-foreground" aria-hidden />
      </header>
      <div className="px-3.5 py-2.5">
        <Legenda texto={publicacao.legenda} linhas={3} />
      </div>
      <Midia
        publicacao={publicacao}
        slides={slides}
        aspecto={publicacao.tipo === "reels" ? "aspect-[9/16] max-h-[420px]" : "aspect-[4/5]"}
      />
      <div
        className="grid grid-cols-3 border-t border-border-subtle text-[12px] font-medium text-muted-foreground"
        aria-hidden
      >
        <span className="flex items-center justify-center gap-1.5 py-2.5">
          <ThumbsUp className="size-4" /> Curtir
        </span>
        <span className="flex items-center justify-center gap-1.5 py-2.5">
          <MessageCircle className="size-4" /> Comentar
        </span>
        <span className="flex items-center justify-center gap-1.5 py-2.5">
          <Share2 className="size-4" /> Compartilhar
        </span>
      </div>
    </Moldura>
  );
}

function PreviaX({ publicacao, slides, conta }: Props) {
  const usuario = conta.usuario ?? conta.nome.toLowerCase().replace(/\s+/g, "");
  return (
    <Moldura>
      <div className="flex gap-2.5 p-3.5">
        <Avatar conta={conta} tamanho="size-9" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] leading-tight">
            <span className="font-semibold">{conta.nome}</span>{" "}
            <span className="text-muted-foreground">@{usuario}</span>
          </p>
          <div className="mt-1">
            <Legenda texto={publicacao.legenda} linhas={3} />
          </div>
          {slides.length > 0 ? (
            <Midia
              publicacao={publicacao}
              slides={slides}
              aspecto="aspect-[16/10]"
              className="mt-2.5 overflow-hidden rounded-2xl border border-border-subtle"
            />
          ) : null}
          <div
            className="mt-3 flex items-center justify-between pr-4 text-muted-foreground"
            aria-hidden
          >
            <MessageCircle className="size-4" />
            <Repeat2 className="size-4" />
            <Heart className="size-4" />
            <BarChart2 className="size-4" />
          </div>
        </div>
      </div>
    </Moldura>
  );
}

function PreviaTikTok({ publicacao, slides, conta }: Props) {
  const usuario = conta.usuario ?? conta.nome;
  return (
    <Moldura>
      <Midia
        publicacao={publicacao}
        slides={slides}
        aspecto="aspect-[9/16] max-h-[460px]"
      />
      <div className="flex items-start gap-2.5 px-3 py-3">
        <Avatar conta={conta} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold">@{usuario}</p>
          <div className="mt-0.5">
            <Legenda texto={publicacao.legenda} />
          </div>
          <p className="mt-1.5 flex items-center gap-1 text-[11px] text-muted-foreground">
            <Music2 className="size-3" aria-hidden /> Som original
          </p>
        </div>
      </div>
    </Moldura>
  );
}
